import React from 'react';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import ReferencesAndAuthor from '../components/ReferencesAndAuthor';
import Footer from '../components/Footer';

function ComoAvaliar() {
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      <Header />
      <Breadcrumb />
      <main className="max-w-5xl mx-auto px-4 py-12">
        <span className="section-kicker">Aplicação prática</span>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mt-2 mb-4 text-slate-800">Como avaliar cada componente da Escala de Coma de Glasgow</h1>
        <p className="text-lg text-gray-600 mb-8">Observe, estimule de forma padronizada e registre o melhor comportamento observado em abertura ocular, resposta verbal e resposta motora. Quando houver fator de interferência, use NT em vez de inventar um valor.</p>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Como avaliar a abertura ocular (O)?</h2>
          <p className="text-gray-700 mb-3">Observe primeiro se o paciente abre os olhos espontaneamente. Se não, use estímulo sonoro; persistindo ausência de resposta, aplique pressão padronizada.</p>
          <p className="text-gray-700"><strong>Locais de pressão:</strong> pressão na extremidade dos dedos, pinçamento do trapézio e incisura supraorbitária. Evite estímulos não padronizados.</p>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Como avaliar a resposta verbal (V)?</h2>
          <p className="text-gray-700">Avalie orientação e qualidade da produção verbal. Resposta orientada pontua 5; conversação confusa 4; palavras isoladas inteligíveis 3; sons ou gemidos 2; ausência 1. Quando afasia, intubação ou outro fator impedir avaliação verbal confiável, registre V(NT).</p>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Como avaliar a resposta motora (M)?</h2>
          <p className="text-gray-700">Peça uma ação simples. Se não houver resposta adequada, aplique pressão e diferencie localização, flexão normal, flexão anormal, extensão e ausência. Bloqueio neuromuscular, sedação profunda ou limitação periférica podem tornar o componente não testável.</p>
        </section>

        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-bold mb-4">Quais são as armadilhas mais comuns?</h2>
          <ul className="space-y-3 text-gray-800">
            <li><strong>Sedação:</strong> opioides, benzodiazepínicos e outros sedativos podem reduzir respostas observáveis. Registre o contexto.</li>
            <li><strong>Hipoglicemia, hipóxia e hipotermia:</strong> podem alterar o nível de consciência sem definir, por si, a causa estrutural.</li>
            <li><strong>Afasia expressiva não é rebaixamento de consciência — mas também não se compensa.</strong> Paciente com lesão de Broca pode compreender comandos e não produzir fala. A resposta verbal continua sendo pontuada pela produção verbal efetivamente observada. Se a afasia impede avaliação verbal confiável, registre <strong>V(NT)</strong> e anote o fator de interferência. <strong>Nunca atribua V4 ou V5 por compreensão preservada</strong> — a compreensão é documentada no exame neurológico, não convertida em pontos da ECG.</li>
            <li><strong>Trauma facial ou edema periorbital:</strong> se impede avaliação ocular, registre O(NT), não O1.</li>
            <li><strong>Intubação ou traqueostomia:</strong> registre V(NT).</li>
            <li><strong>Paralisia ou bloqueio neuromuscular:</strong> podem tornar M não testável.</li>
            <li><strong>Escore isolado:</strong> não substitui exame neurológico, contexto, tendência temporal ou avaliação de via aérea.</li>
          </ul>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-4">Exemplos clínicos</h2>
          <div className="space-y-5">
            <article className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-2">Caso 1: trauma cranioencefálico</h3>
              <p className="text-gray-700">Homem, 45 anos, vítima de queda de altura, TCE fechado. Abre os olhos ao chamado (O3), responde confuso (V4) e obedece comando motor (M6). <strong>ECG = 13.</strong> O número não substitui a avaliação clínica e a evolução seriada.</p>
            </article>

            <article className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-2">Caso 2: intoxicação</h3>
              <p className="text-gray-700">Mulher, 32 anos. Olhos abrem à pressão (O2), emite <strong>palavras isoladas inteligíveis, sem formar frases (V3)</strong> e apresenta flexão normal (M4). <strong>ECG = 9.</strong> Registre a causa presumida e acompanhe a tendência.</p>
            </article>

            <article className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-2">Caso 3: AVC com afasia de expressão</h3>
              <p className="text-gray-700">Homem, 67 anos, AVC isquêmico em território de Broca. Abre os olhos espontaneamente (O4). Não emite palavras; compreende e executa “levante o braço” (M6). Componente verbal não avaliável de forma confiável → <strong>V(NT)</strong>.</p>
              <p className="text-gray-700 mt-2">Registro correto: <strong>O4 V(NT) M6 — afasia de expressão, compreensão preservada.</strong> Não some o total. Documente a compreensão preservada em texto livre.</p>
            </article>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h2 className="text-2xl font-bold mb-3">Como registrar Glasgow no prontuário?</h2>
          <p className="text-gray-700 mb-4">Registre horário, componentes e fatores de interferência. O total é secundário ao padrão O/V/M.</p>
          <div className="bg-slate-50 rounded-xl p-4 font-mono text-sm space-y-2">
            <p>08:30 — O3 V4 M5 = ECG 12. Responde confuso. Sem sedação.</p>
            <p>10:15 — O4 V5 M6 = ECG 15. Alerta e orientado.</p>
            <p>11:40 — O4 V(NT) M6 — intubado. Total não somado.</p>
          </div>
        </section>

        <section className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-3">Como avaliar Glasgow em paciente sedado, intubado ou afásico?</h2>
          <p className="text-slate-100">Marque como NT apenas o componente cuja avaliação foi inviabilizada. Intubação interfere na resposta verbal; edema periorbital pode impedir abertura ocular; bloqueio neuromuscular pode impedir resposta motora. Não transforme ausência de testabilidade em pontuação 1 e não some um total artificial.</p>
        </section>

        <nav className="flex flex-wrap gap-4 mt-8 text-sm font-semibold" aria-label="Conteúdo relacionado">
          <a href="/#calculadora" className="text-teal-700">→ Abrir calculadora</a>
          <a href="/tabela" className="text-teal-700">→ Tabela completa</a>
          <a href="/pediatrica" className="text-teal-700">→ Glasgow pediátrica</a>
          <a href="/ecg-p" className="text-teal-700">→ ECG-P</a>
        </nav>

        <ReferencesAndAuthor />
      </main>
      <Footer />
    </div>
  );
}

export default ComoAvaliar;
